/**
 * ICR (In-Country Representation) view model for /product-insights/icr.
 *
 * A rep in a country files a cumulative monthly report for a university; the
 * figures are SELF-REPORTED by that rep. EMERGE proxies the read through
 * `GET /product-insights/icr` (camelCase, `{data, meta}`), nothing is stored.
 *
 * - The wire is read DEFENSIVELY into these view types (`toIcrView`). The page
 *   never trusts a key to be present: a missing figure is `null` and renders
 *   an em dash, never `0`.
 * - `monthsCovered` arrives as a count or as a list of `YYYY-MM` months; both
 *   are kept so the row can state the span as a fact.
 * - Month-by-month figures (`groupBy=month`) may sit on each university row or
 *   at the top level; `monthsFor` resolves which, and answers `null` when the
 *   response cannot be attributed to the row (never somebody else's months).
 */

import type { IcrParams, IcrStatus } from '@/lib/types';

export type { IcrStatus };

export const ICR_STATUSES: readonly IcrStatus[] = ['published', 'submitted', 'all-final'];

export const ICR_STATUS_LABEL: Record<IcrStatus, string> = {
  published: 'Published',
  submitted: 'Submitted',
  'all-final': 'All final',
};

export const DEFAULT_ICR_STATUS: IcrStatus = 'published';

/** The wire params, from `@/lib/types`. */
export type IcrRequestParams = IcrParams;

/** The seven funnel figures plus the in-progress count, all nullable. */
export interface IcrViewMetrics {
  leadsGenerated: number | null;
  leadsEngaged: number | null;
  interestedNextIntake: number | null;
  applicationsInProgress: number | null;
  applicationsSubmitted: number | null;
  admitted: number | null;
  offersAccepted: number | null;
  enrolled: number | null;
}

export const METRIC_KEYS = [
  'leadsGenerated',
  'leadsEngaged',
  'interestedNextIntake',
  'applicationsInProgress',
  'applicationsSubmitted',
  'admitted',
  'offersAccepted',
  'enrolled',
] as const satisfies readonly (keyof IcrViewMetrics)[];

export type IcrMetricKey = (typeof METRIC_KEYS)[number];

export const METRIC_LABEL: Record<IcrMetricKey, string> = {
  leadsGenerated: 'Leads generated',
  leadsEngaged: 'Leads engaged',
  interestedNextIntake: 'Interested next intake',
  applicationsInProgress: 'Applications in progress',
  applicationsSubmitted: 'Applications submitted',
  admitted: 'Admitted',
  offersAccepted: 'Offers accepted',
  enrolled: 'Enrolled',
};

/** A count, or the months themselves (`YYYY-MM`), or nothing reported. */
export interface IcrMonthsCovered {
  count: number | null;
  months: string[];
}

export interface IcrViewOverall extends IcrViewMetrics {
  reports: number | null;
  representatives: number | null;
  universities: number | null;
  monthsCovered: IcrMonthsCovered;
}

export interface IcrViewMonth extends IcrViewMetrics {
  month: string;
  reports: number | null;
}

export interface IcrCompanyRef {
  id: number;
  name: string;
}

export interface IcrViewRow extends IcrViewMetrics {
  /** Stable React key: university + rep country. */
  key: string;
  universityCompanyId: number | null;
  universityId: string | null;
  universityName: string;
  schools: { schoolId: string | null; schoolName: string }[];
  repCountry: string | null;
  repCountryCode: string | null;
  representatives: number | null;
  reports: number | null;
  monthsCovered: IcrMonthsCovered;
  /**
   * The linked EMERGE company (staff-confirmed school link, every linked school
   * agreeing), else null. `universityCompanyId` is the SEED platform's id, NOT
   * `d_companies.id`: it is only the `companyId` filter, never a link.
   */
  company: IcrCompanyRef | null;
  /** Present only on a `groupBy=month` response. */
  byMonth: IcrViewMonth[] | null;
}

export interface IcrView {
  overall: IcrViewOverall;
  rows: IcrViewRow[];
  /** Top-level month rows, on a `groupBy=month` response. */
  byMonth: IcrViewMonth[] | null;
  /** When EMERGE (or upstream) produced the figures; null when not reported. */
  fetchedAt: string | null;
}

/* --- defensive readers ------------------------------------------------------ */

type Loose = Record<string, unknown>;

function asObject(value: unknown): Loose | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Loose) : null;
}

function num(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
}

function str(value: unknown): string | null {
  if (typeof value === 'string' && value.trim() !== '') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function first(obj: Loose, ...keys: string[]): unknown {
  for (const key of keys) if (obj[key] !== undefined) return obj[key];
  return undefined;
}

function metricsOf(obj: Loose): IcrViewMetrics {
  return {
    leadsGenerated: num(first(obj, 'totalLeadsGenerated', 'leadsGenerated')),
    leadsEngaged: num(first(obj, 'totalLeadsEngaged', 'leadsEngaged')),
    interestedNextIntake: num(obj.interestedNextIntake),
    applicationsInProgress: num(obj.applicationsInProgress),
    applicationsSubmitted: num(obj.applicationsSubmitted),
    admitted: num(obj.admitted),
    offersAccepted: num(obj.offersAccepted),
    enrolled: num(obj.enrolled),
  };
}

const MONTH_PATTERN = /^\d{4}-\d{2}/;

function monthsCoveredOf(value: unknown): IcrMonthsCovered {
  if (Array.isArray(value)) {
    const months = value
      .map((entry) => str(entry) ?? str(asObject(entry)?.month))
      .filter((entry): entry is string => entry !== null && MONTH_PATTERN.test(entry))
      .map((entry) => entry.slice(0, 7))
      .sort();
    return { count: months.length, months };
  }
  return { count: num(value), months: [] };
}

function monthRowsOf(value: unknown): IcrViewMonth[] | null {
  if (!Array.isArray(value)) return null;
  const rows: IcrViewMonth[] = [];
  for (const entry of value) {
    const obj = asObject(entry);
    if (obj === null) continue;
    const month = str(first(obj, 'month', 'reportMonth', 'period'));
    if (month === null) continue;
    rows.push({ month, reports: num(obj.reports), ...metricsOf(obj) });
  }
  return rows.sort((a, b) => a.month.localeCompare(b.month));
}

/** Top-level month rows keep their attribution keys so `monthsFor` can match them. */
interface AttributedMonth extends IcrViewMonth {
  universityCompanyId: number | null;
  universityId: string | null;
  repCountryCode: string | null;
}

function attributedMonthsOf(value: unknown): AttributedMonth[] | null {
  if (!Array.isArray(value)) return null;
  const rows: AttributedMonth[] = [];
  for (const entry of value) {
    const obj = asObject(entry);
    if (obj === null) continue;
    const month = str(first(obj, 'month', 'reportMonth', 'period'));
    if (month === null) continue;
    rows.push({
      month,
      reports: num(obj.reports),
      ...metricsOf(obj),
      universityCompanyId: num(obj.universityCompanyId),
      universityId: str(obj.universityId),
      repCountryCode: str(obj.repCountryCode),
    });
  }
  return rows.sort((a, b) => a.month.localeCompare(b.month));
}

function companyOf(value: unknown): IcrCompanyRef | null {
  const obj = asObject(value);
  if (obj === null) return null;
  const id = num(obj.id);
  const name = str(obj.name);
  return id === null || name === null ? null : { id, name };
}

function universityRowOf(entry: unknown, index: number): IcrViewRow | null {
  const obj = asObject(entry);
  if (obj === null) return null;
  const universityCompanyId = num(obj.universityCompanyId);
  const universityId = str(obj.universityId);
  const repCountryCode = str(obj.repCountryCode);
  const repCountry = str(obj.repCountry);
  const schools = Array.isArray(obj.schools)
    ? obj.schools
        .map((school) => {
          const s = asObject(school);
          const schoolName = s === null ? null : str(s.schoolName);
          return schoolName === null ? null : { schoolId: str(s?.schoolId), schoolName };
        })
        .filter((s): s is { schoolId: string | null; schoolName: string } => s !== null)
    : [];
  const identity = universityCompanyId ?? universityId ?? `row-${index}`;
  return {
    key: `${identity}:${repCountryCode ?? repCountry ?? '-'}`,
    universityCompanyId,
    universityId,
    universityName: str(obj.universityName) ?? 'Unnamed university',
    schools,
    repCountry,
    repCountryCode,
    representatives: num(obj.representatives),
    reports: num(obj.reports),
    monthsCovered: monthsCoveredOf(obj.monthsCovered),
    company: companyOf(obj.company),
    byMonth: monthRowsOf(obj.byMonth),
    ...metricsOf(obj),
  };
}

/**
 * Reads the proxy's answer — `{data, meta}` or the bare `data` — into the view.
 * Nothing here invents a figure: a key the response lacks is `null`.
 */
export function toIcrView(raw: unknown): IcrView & { attributedMonths: AttributedMonth[] | null } {
  const root = asObject(raw) ?? {};
  const dataCandidate = asObject(root.data);
  const data = dataCandidate !== null && (dataCandidate.overall !== undefined || dataCandidate.byUniversity !== undefined)
    ? dataCandidate
    : root;
  const meta = asObject(root.meta) ?? asObject(data.meta) ?? {};
  const overallObj = asObject(data.overall) ?? {};
  const rows = (Array.isArray(data.byUniversity) ? data.byUniversity : [])
    .map(universityRowOf)
    .filter((row): row is IcrViewRow => row !== null);
  return {
    overall: {
      reports: num(overallObj.reports),
      representatives: num(overallObj.representatives),
      universities: num(overallObj.universities),
      monthsCovered: monthsCoveredOf(overallObj.monthsCovered),
      ...metricsOf(overallObj),
    },
    rows,
    byMonth: monthRowsOf(data.byMonth),
    attributedMonths: attributedMonthsOf(data.byMonth),
    fetchedAt:
      str(root.fetchedAt) ?? str(data.fetchedAt) ?? str(meta.fetchedAt) ?? str(meta.generatedAt),
  };
}

/**
 * The month rows for one university row out of a `groupBy=month` answer, or
 * `null` when the answer cannot be attributed to it.
 *
 * Order of trust: the matching university row's own `byMonth`, then top-level
 * rows carrying a matching id, then — only when the request was narrowed to
 * this company and nothing else came back — the top-level rows as they are.
 */
export function monthsFor(
  row: IcrViewRow,
  view: ReturnType<typeof toIcrView>,
  narrowedToCompany: boolean,
): IcrViewMonth[] | null {
  const sameUniversity = (candidate: {
    universityCompanyId: number | null;
    universityId: string | null;
  }) =>
    (row.universityCompanyId !== null && candidate.universityCompanyId === row.universityCompanyId) ||
    (row.universityId !== null && candidate.universityId === row.universityId);

  const match = view.rows.find(
    (candidate) =>
      sameUniversity(candidate) &&
      (row.repCountryCode === null ||
        candidate.repCountryCode === null ||
        candidate.repCountryCode === row.repCountryCode),
  );
  if (match?.byMonth) return match.byMonth;

  const attributed = view.attributedMonths;
  if (attributed !== null && attributed.some((m) => m.universityCompanyId !== null || m.universityId !== null)) {
    return attributed.filter(
      (m) =>
        sameUniversity(m) &&
        (row.repCountryCode === null || m.repCountryCode === null || m.repCountryCode === row.repCountryCode),
    );
  }

  if (narrowedToCompany && view.byMonth !== null && view.rows.length <= 1) return view.byMonth;
  return null;
}

/* --- formatting ------------------------------------------------------------- */

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `2026-03` → `Mar 2026`; anything else is returned as it came. Never a `Date`. */
export function formatMonth(month: string): string {
  const match = /^(\d{4})-(\d{2})/.exec(month);
  if (!match) return month;
  const name = MONTH_NAMES[Number(match[2]) - 1];
  return name === undefined ? month : `${name} ${match[1]}`;
}

/** "6 months", "1 month", or null when nothing was reported. */
export function monthsCoveredText(covered: IcrMonthsCovered): string | null {
  if (covered.count === null) return null;
  return covered.count === 1 ? '1 month' : `${covered.count} months`;
}

/** "Mar 2026 – Aug 2026" when the months themselves were sent, else null. */
export function monthsSpanText(covered: IcrMonthsCovered): string | null {
  const firstMonth = covered.months[0];
  const lastMonth = covered.months[covered.months.length - 1];
  if (firstMonth === undefined || lastMonth === undefined) return null;
  return firstMonth === lastMonth
    ? formatMonth(firstMonth)
    : `${formatMonth(firstMonth)} – ${formatMonth(lastMonth)}`;
}

/**
 * `part / whole` as a 0–1 rate, or null when either figure was not reported or
 * the whole is not positive (never a made-up 0%).
 */
export function icrRate(part: number | null, whole: number | null): number | null {
  if (part === null || whole === null || whole <= 0) return null;
  return part / whole;
}

export function parseIcrStatus(raw: string | null): IcrStatus {
  return raw === 'submitted' || raw === 'all-final' ? raw : DEFAULT_ICR_STATUS;
}
